import { z } from 'zod';
import {
  spoItemSchema,
  spoOptionSchema,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/spo.ts';
import { CustomEventWithoutDetail, html, View } from 'rune-ts';
import { Input_TextEditor } from '@services/MarppleShopStudio/shared/component/atom/Input/Input_TextEditor.ts';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import klass from './Input_AddOption.module.scss';
import { font } from '@services/MarppleShop/shared/typography';

export class ChangeOption extends CustomEventWithoutDetail {}
export type SpoOption = z.infer<typeof spoOptionSchema>;
export type SpoItem = z.infer<typeof spoItemSchema>;

export class InputOption extends View<z.infer<typeof spoOptionSchema>> {
  private optionName = new Input_TextEditor({
    name: formKeyMap.spo_items,
    value: this.data.name ?? '',
    title: '옵션명',
    onInput: this.dispatchChangeOptionEvent.bind(this),
    font_klass: font('14_medium'),
    option: {
      placeholder: '예시) 컬러',
    },
  });

  private optionValue = new Input_TextEditor({
    name: formKeyMap.spo_items,
    value: this.data._.spo_option_values.map(({ name }) => name).join(',') ?? '',
    title: '옵션값',
    onInput: () => {
      const currentValue = this.optionValue.getValue();
      const validatedValue = this.validateDuringInput(currentValue);

      // 값이 실제로 변경된 경우에만 setValue 호출
      if (currentValue !== validatedValue) {
        this.optionValue.setValue(validatedValue);
      }

      this.dispatchChangeOptionEvent();
    },
    font_klass: font('14_medium'),
    option: {
      placeholder: '예시) 블랙, 블루 등 콤마 ‘,’로 구분',
    },
  });

  override template() {
    return html` <div class="${klass.option}">${this.optionName} ${this.optionValue}</div> `;
  }

  private dispatchChangeOptionEvent() {
    this.dispatchEvent(ChangeOption, {
      bubbles: true,
    });
  }

  private cleanCommaString(input: string): string {
    return input
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item !== '')
      .join(',');
  }

  private validateDuringInput(input: string): string {
    return (
      input
        // 연속된 콤마(2개 이상)를 하나로 정리
        .replace(/,{2,}/g, ',')
        // 맨 앞의 콤마들 제거
        .replace(/^,+/g, '')
    );
  }

  getOptionInfo(): SpoOption {
    this.optionValue.setValue(this.cleanCommaString(this.optionValue.getValue()));
    return {
      name: this.optionName.getValue(),
      _: {
        spo_option_values: this.optionValue
          .getValue()
          .split(',')
          .map((value) => ({
            name: value,
          })),
      },
    };
  }

  isAllEmpty(): boolean {
    return !this.optionName.getValue() && !this.optionValue.getValue();
  }

  isCompleted(): boolean {
    return !!this.optionName.getValue() && !!this.optionValue.getValue();
  }

  removeOptionInfo() {
    this.data.name = '';
    this.data._.spo_option_values = [];
    this.optionName.setValue('');
    this.optionValue.setValue('');
    this.redraw();
    return this;
  }
}
